import { useEffect, useState } from 'react';

/**
 * Sub-screen transition hook.
 *
 * Returns a `visible` boolean that briefly flips to false on every
 * screen-index change and then back to true 30ms later. Pair with
 * a re-keyed wrapper to get a clean fade-up on each sub-screen
 * change:
 *
 *   const visible = useScreenTransition(currentScreen);
 *   return (
 *     <div key={currentScreen} className="screen-enter">
 *       ...
 *     </div>
 *   );
 *
 * The key is what actually remounts the subtree and restarts the
 * CSS animation; the hook's `visible` flag is available for callers
 * that want to gate other one-shot effects (sound, haptics) on the
 * same transition boundary without double-firing.
 */
export function useScreenTransition(screenIndex) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, [screenIndex]);

  return visible;
}
